import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  MAX_MESSAGE_CHARS,
  canEditMessage,
  renderMentions,
  type ChatMessageView,
  type ConversationView,
} from '@goyone/shared';
import { api } from '../../lib/api';
import { dateTime, relative, time } from '../../lib/format';
import { useAuth, useSession } from '../../auth/AuthProvider';
import { Icon } from '../../components/Icon';
import { Badge, ErrorState, Modal } from '../../components/ui';
import { Avatar, useTeamDirectory } from '../../components/People';
import { useToast } from '../../components/Toast';
import {
  MessageMenu,
  ForwardPicker,
  copyText,
  buildReplyQuote,
  type MsgAction,
} from '../../components/MessageMenu';

/**
 * الشات الداخلي.
 *
 * ## بالسحب لا بالدفع
 *
 * لا مقبس ويب. الدفع الفوري يحتاج اتصالًا حيًّا وحالةً في ذاكرة العملية،
 * ونسختان من الخادم تعنيان أن رسالة كُتبت على إحداهما لا تصل إلى من يستمع
 * على الأخرى — عطبٌ صامت لا يظهر إلا في الإنتاج.
 *
 * والسحب المتقارب يعمل في كل نشر. و`react-query` توقفه حين يغيب التبويب
 * افتراضيًّا، فلا يسأل متصفّحٌ مغلقٌ عن رسائل لا يقرؤها أحد.
 */

/** المحادثة المفتوحة تُسأل كل ثلاث ثوانٍ، والقائمة كل عشر. */
const OPEN_MS = 3000;
const LIST_MS = 10_000;

export function Chat() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [picking, setPicking] = useState(false);

  const list = useQuery({
    queryKey: ['chat', 'conversations'],
    queryFn: () => api<ConversationView[]>('/chat/conversations'),
    refetchInterval: LIST_MS,
  });

  const conversations = list.data ?? [];
  const active = conversations.find((c) => c.id === id) ?? null;

  /* أوّل محادثة تُفتح تلقائيًّا: شاشةٌ فارغة لا تقول ماذا يفعل من يفتحها. */
  useEffect(() => {
    if (!id && conversations.length) navigate(`/chat/${conversations[0]!.id}`, { replace: true });
  }, [id, conversations, navigate]);

  return (
    <div className="page chat-page">
      <aside className="chat-side">
        <div className="chat-side-head">
          {/* إغلاقُ ملءِ الشاشةِ مباشرةً — علامةُ غلقٍ لا رجوع. */}
          <button
            className="btn btn-sm btn-icon btn-ghost"
            onClick={() => navigate(-1)}
            title="إغلاق المحادثات"
            aria-label="إغلاق"
          >
            <Icon name="x" size={17} />
          </button>
          <h1 className="chat-heading">المحادثات</h1>
          <div className="row" style={{ gap: 4 }}>
            <button
              className="btn btn-sm btn-icon btn-ghost"
              onClick={() => setPicking(true)}
              title="رسالة مباشرة"
              aria-label="رسالة مباشرة"
            >
              <Icon name="user" size={15} />
            </button>
            <button
              className="btn btn-sm btn-icon btn-ghost"
              onClick={() => setCreating(true)}
              title="قناة جديدة"
              aria-label="قناة جديدة"
            >
              <Icon name="plus" size={15} />
            </button>
          </div>
        </div>

        {list.isError && <ErrorState error={list.error} onRetry={() => void list.refetch()} />}

        {list.data && conversations.length === 0 && (
          <p className="tiny mute center mt-4">
            لا محادثات بعد. أنشئ قناة أو ابدأ رسالة مباشرة مع زميل.
          </p>
        )}

        <ul className="chat-list">
          {conversations.map((c) => (
            <li key={c.id}>
              <button
                className={`chat-row${c.id === id ? ' is-active' : ''}`}
                onClick={() => navigate(`/chat/${c.id}`)}
              >
                {c.kind === 'DIRECT' ? (
                  <Avatar name={c.title} size={30} />
                ) : (
                  <span className="chat-hash" aria-hidden="true">
                    {c.isPrivate ? <Icon name="user" size={14} /> : '#'}
                  </span>
                )}

                <span className="chat-row-main">
                  <span className="chat-row-top">
                    <strong className="chat-row-title">{c.title}</strong>
                    {c.lastAt && <span className="tiny mute">{time(c.lastAt)}</span>}
                  </span>
                  <span className="tiny mute chat-row-last">
                    {c.lastBody
                      ? `${c.lastAuthorName ? `${c.lastAuthorName.split(' ')[0]}: ` : ''}${c.lastBody}`
                      : 'لا رسائل بعد'}
                  </span>
                </span>

                {c.unread > 0 && <span className="chat-unread num">{c.unread}</span>}
              </button>
            </li>
          ))}
        </ul>
      </aside>

      <section className="chat-main">
        {active ? (
          <Room conversation={active} />
        ) : (
          <div className="chat-blank">
            <Icon name="chat" size={30} />
            <p className="mute">اختر محادثة، أو ابدأ واحدة.</p>
          </div>
        )}
      </section>

      {creating && (
        <ChannelModal
          onClose={() => setCreating(false)}
          onCreated={(conv) => {
            setCreating(false);
            void queryClient.invalidateQueries({ queryKey: ['chat'] });
            navigate(`/chat/${conv.id}`);
          }}
        />
      )}

      {picking && (
        <DirectModal
          onClose={() => setPicking(false)}
          onOpened={(conv) => {
            setPicking(false);
            void queryClient.invalidateQueries({ queryKey: ['chat'] });
            navigate(`/chat/${conv.id}`);
          }}
        />
      )}
    </div>
  );
}

/* ==========================================================================
   المحادثة المفتوحة
   ========================================================================== */

function Room({ conversation }: { conversation: ConversationView }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const session = useSession();
  const { atLeast } = useAuth();
  const { byId, assignable } = useTeamDirectory();

  const [draft, setDraft] = useState('');
  const [editing, setEditing] = useState<string | null>(null);
  const [editBody, setEditBody] = useState('');
  const [mentionOpen, setMentionOpen] = useState(false);
  /* الرسالةُ المُحوَّلةُ — تُنتقى لها وجهةٌ ثم تُرسَل نصُّها إليها. */
  const [forwarding, setForwarding] = useState<ChatMessageView | null>(null);
  const bottom = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLTextAreaElement>(null);

  /* قائمةُ المحادثاتِ لوجهةِ التحويل — من ذاكرةِ الاستعلامِ نفسِها لا جلبٌ ثانٍ. */
  const convos = useQuery({
    queryKey: ['chat', 'conversations'],
    queryFn: () => api<ConversationView[]>('/chat/conversations'),
  });

  const messages = useQuery({
    queryKey: ['chat', 'messages', conversation.id],
    queryFn: () => api<ChatMessageView[]>(`/chat/conversations/${conversation.id}/messages`),
    refetchInterval: OPEN_MS,
  });

  const rows = useMemo(() => messages.data ?? [], [messages.data]);

  /*
   * القراءة تُعلَّم عند الفتح وعند وصول جديد.
   *
   * لا مع كل نبضة سحب: كتابةٌ كل ثلاث ثوانٍ لكل مستخدم مفتوحٍ عنده الشات
   * حملٌ بلا مقابل. والمفتاح هو معرّف آخر رسالة — يتغيّر حين يصل جديد لا
   * قبله.
   */
  const lastId = rows.length ? rows[rows.length - 1]!.id : null;

  useEffect(() => {
    if (!lastId) return;
    void api(`/chat/conversations/${conversation.id}/read`, { method: 'POST' }).then(() =>
      queryClient.invalidateQueries({ queryKey: ['chat', 'conversations'] }),
    );
  }, [conversation.id, lastId, queryClient]);

  /* والشاشة تنزل إلى الأسفل: الجديد في الأسفل كما في كل شات. */
  useEffect(() => {
    bottom.current?.scrollIntoView({ block: 'end' });
  }, [lastId, conversation.id]);

  const send = useMutation({
    mutationFn: (body: string) =>
      api<ChatMessageView>(`/chat/conversations/${conversation.id}/messages`, {
        method: 'POST',
        body: { body },
      }),
    onSuccess: () => {
      setDraft('');
      void queryClient.invalidateQueries({ queryKey: ['chat'] });
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الإرسال.'),
  });

  const edit = useMutation({
    mutationFn: (args: { id: string; body: string }) =>
      api(`/chat/messages/${args.id}`, { method: 'PATCH', body: { body: args.body } }),
    onSuccess: () => {
      setEditing(null);
      void queryClient.invalidateQueries({ queryKey: ['chat'] });
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر التعديل.'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api(`/chat/messages/${id}`, { method: 'DELETE' }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['chat'] }),
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحذف.'),
  });

  /* التحويلُ إرسالٌ إلى محادثةٍ أخرى بنصِّ الرسالةِ نفسِه. */
  const forward = useMutation({
    mutationFn: (args: { toId: string; body: string }) =>
      api(`/chat/conversations/${args.toId}/messages`, {
        method: 'POST',
        body: { body: args.body },
      }),
    onSuccess: () => {
      setForwarding(null);
      toast('حُوّلت الرسالة');
      void queryClient.invalidateQueries({ queryKey: ['chat'] });
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر التحويل.'),
  });

  /* ردٌّ باقتباسِ الرسالةِ في حقلِ الكتابة، ونسخٌ إلى الحافظة. */
  const replyTo = (m: ChatMessageView) => {
    setDraft(buildReplyQuote(m.authorName, m.body));
    input.current?.focus();
  };
  const copyMsg = (m: ChatMessageView) =>
    void copyText(m.body).then((ok) => toast(ok ? 'نُسخت الرسالة' : 'تعذّر النسخ'));

  const submit = () => {
    const body = draft.trim();
    if (!body) return;
    send.mutate(body);
  };

  const nameOf = (userId: string) => byId.get(userId)?.name;

  /* أفعالُ الرسالةِ في قائمتها — ردٌّ ونسخٌ وتحويلٌ للجميع، وتعديلٌ وحذفٌ بحسبِ الحقّ. */
  const buildActions = (m: ChatMessageView): MsgAction[] => {
    const mine = m.authorId === session.user.id;
    const acts: MsgAction[] = [
      { key: 'reply', label: 'ردّ', icon: 'reply', onClick: () => replyTo(m) },
      { key: 'copy', label: 'نسخ', icon: 'copy', onClick: () => copyMsg(m) },
      { key: 'forward', label: 'تحويل', icon: 'forward', onClick: () => setForwarding(m) },
    ];
    if (mine && canEditMessage(m.createdAt)) {
      acts.push({
        key: 'edit',
        label: 'تعديل',
        icon: 'edit',
        onClick: () => {
          setEditing(m.id);
          setEditBody(m.body);
        },
      });
    }
    if (mine || (atLeast('ADMIN') && conversation.kind === 'CHANNEL')) {
      acts.push({
        key: 'delete',
        label: 'حذف',
        icon: 'trash',
        danger: true,
        onClick: () => remove.mutate(m.id),
      });
    }
    return acts;
  };

  return (
    <>
      <header className="chat-head">
        <div>
          <h2 className="chat-title">
            {conversation.kind === 'CHANNEL' && (
              <span className="mute">{conversation.isPrivate ? '🔒 ' : '# '}</span>
            )}
            {conversation.title}
          </h2>
          {conversation.topic && <p className="tiny mute">{conversation.topic}</p>}
        </div>

        {conversation.kind === 'CHANNEL' && (
          <div className="row" style={{ gap: 'var(--sp-2)' }}>
            <Badge kind={conversation.isPrivate ? 'neutral' : 'info'}>
              {conversation.isPrivate ? 'خاصة' : 'عامة'}
            </Badge>
            <span className="tiny mute">{conversation.memberCount} عضوًا</span>
          </div>
        )}
      </header>

      <div className="chat-stream">
        {messages.isPending && <div className="skeleton" style={{ height: 120 }} />}
        {messages.isError && (
          <ErrorState error={messages.error} onRetry={() => void messages.refetch()} />
        )}

        {messages.data && rows.length === 0 && (
          <p className="tiny mute center">لا رسائل بعد. اكتب أول واحدة.</p>
        )}

        {rows.map((m, i) => {
          const mine = m.authorId === session.user.id;
          /* رسائل متتابعة من شخصٍ واحد تُجمع: اسمٌ فوق كل سطر يُغرق الشاشة. */
          /* وبعد حدثٍ يُكتب الاسم ثانيةً: الحدثُ يقطع التتابع وإن كان فاعلُه الكاتبَ نفسه. */
          const grouped =
            i > 0 &&
            !rows[i - 1]!.system &&
            rows[i - 1]!.authorId === m.authorId &&
            !m.deletedAt;

          if (m.deletedAt) {
            return (
              <p key={m.id} className="chat-msg chat-deleted tiny mute">
                حُذفت رسالة
              </p>
            );
          }

          /* حدثُ المجموعة سطرٌ في المنتصف — لا كاتبَ فوقه ولا تعديلَ ولا حذف. */
          if (m.system) {
            return (
              <p key={m.id} className="chat-msg chat-deleted tiny mute" title={dateTime(m.createdAt)}>
                {m.body}
                {m.authorName ? ` — ${m.authorName}` : ''}
              </p>
            );
          }

          return (
            <article key={m.id} className={`chat-msg${grouped ? ' is-grouped' : ''}`}>
              {!grouped && (
                <div className="chat-msg-head">
                  <Avatar name={m.authorName ?? 'مستخدم سابق'} size={26} />
                  <strong className="small">{m.authorName ?? 'مستخدم سابق'}</strong>
                  <span className="tiny mute" title={dateTime(m.createdAt)}>
                    {relative(m.createdAt)}
                  </span>
                  {m.editedAt && <span className="tiny mute">— عُدّلت</span>}
                </div>
              )}

              {editing === m.id ? (
                <div className="chat-edit">
                  <textarea
                    className="textarea"
                    rows={2}
                    value={editBody}
                    onChange={(e) => setEditBody(e.target.value)}
                  />
                  <div className="row" style={{ gap: 'var(--sp-2)' }}>
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() => edit.mutate({ id: m.id, body: editBody })}
                      disabled={!editBody.trim()}
                    >
                      حفظ
                    </button>
                    <button className="btn btn-sm" onClick={() => setEditing(null)}>
                      إلغاء
                    </button>
                  </div>
                </div>
              ) : (
                <div className="chat-body">
                  <p className="chat-text">{renderMentions(m.body, nameOf)}</p>
                  <MessageMenu align="end" actions={buildActions(m)} />
                </div>
              )}
            </article>
          );
        })}

        <div ref={bottom} />
      </div>

      <div className="chat-composer">
        <div className="fb-mention">
          <button
            type="button"
            className="btn btn-sm btn-icon btn-ghost"
            onClick={() => setMentionOpen((v) => !v)}
            title="اذكر زميلًا"
            aria-label="اذكر زميلًا"
            aria-expanded={mentionOpen}
          >
            <Icon name="user" size={15} />
          </button>

          {mentionOpen && (
            <div className="fb-mention-list chat-mention-list">
              {assignable
                .filter((m) => m.userId !== session.user.id)
                .map((m) => (
                  <button
                    key={m.userId}
                    type="button"
                    className="pick-row"
                    onClick={() => {
                      setDraft((b) => `${b}${b && !b.endsWith(' ') ? ' ' : ''}@[${m.userId}] `);
                      setMentionOpen(false);
                    }}
                  >
                    <Avatar name={m.name} src={m.avatarUrl} size={24} />
                    <span className="pick-row-text">
                      <strong>{m.name}</strong>
                    </span>
                  </button>
                ))}
            </div>
          )}
        </div>

        <textarea
          ref={input}
          className="textarea chat-input"
          rows={1}
          value={draft}
          onChange={(e) => setDraft(e.target.value.slice(0, MAX_MESSAGE_CHARS))}
          onKeyDown={(e) => {
            /*
              Enter يُرسل، وShift+Enter يُنزل سطرًا.
              وهو ما يتوقّعه من استعمل أي شات — ومخالفته تُنتج رسائل نصفها
              مُرسَل قبل تمامها.
            */
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
          placeholder={`اكتب رسالة إلى ${conversation.title}…`}
        />

        <button
          className="btn btn-primary btn-icon"
          onClick={submit}
          disabled={send.isPending || !draft.trim()}
          title="إرسال"
          aria-label="إرسال"
        >
          <Icon name="send" size={16} />
        </button>
      </div>

      {forwarding && (
        <ForwardPicker
          conversations={convos.data ?? []}
          busy={forward.isPending}
          onClose={() => setForwarding(null)}
          onPick={(toId) => forward.mutate({ toId, body: forwarding.body })}
        />
      )}
    </>
  );
}

/* ==========================================================================
   قناة جديدة
   ========================================================================== */

function ChannelModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (c: ConversationView) => void;
}) {
  const { assignable } = useTeamDirectory();
  const session = useSession();
  const [name, setName] = useState('');
  const [topic, setTopic] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const [members, setMembers] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      api<ConversationView>('/chat/conversations', {
        method: 'POST',
        body: { name: name.trim(), topic: topic.trim() || null, isPrivate, memberIds: members },
      }),
    onSuccess: onCreated,
    onError: (e) => setError(e instanceof Error ? e.message : 'تعذّر الإنشاء.'),
  });

  return (
    <Modal title="قناة جديدة" onClose={onClose}>
      {error && <div className="alert alert-danger mb-4">{error}</div>}

      <label className="field">
        <span className="label">اسم القناة</span>
        <input
          className="input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="فريق المبيعات"
        />
      </label>

      <label className="field">
        <span className="label">الموضوع (اختياري)</span>
        <input
          className="input"
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
          placeholder="متابعة الصفقات الكبيرة"
        />
      </label>

      <label className="row" style={{ cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={isPrivate}
          onChange={(e) => setIsPrivate(e.target.checked)}
        />
        <span className="small">قناة خاصة — يراها أعضاؤها وحدهم</span>
      </label>

      {/*
        الأعضاء للخاصة وحدها.
        العامة يقرؤها كل من في الشركة، فاختيار أعضاء لها يوهم بحصرٍ لا وجود له.
      */}
      {isPrivate && (
        <div className="field mt-3">
          <span className="label">الأعضاء</span>
          <div className="chat-members">
            {assignable
              .filter((m) => m.userId !== session.user.id)
              .map((m) => (
                <label key={m.userId} className="chat-member">
                  <input
                    type="checkbox"
                    checked={members.includes(m.userId)}
                    onChange={(e) =>
                      setMembers((cur) =>
                        e.target.checked
                          ? [...cur, m.userId]
                          : cur.filter((x) => x !== m.userId),
                      )
                    }
                  />
                  <Avatar name={m.name} src={m.avatarUrl} size={24} />
                  <span className="small">{m.name}</span>
                </label>
              ))}
          </div>
        </div>
      )}

      <div className="row mt-4" style={{ gap: 'var(--sp-2)' }}>
        <button
          className="btn btn-primary"
          onClick={() => {
            setError(null);
            create.mutate();
          }}
          disabled={create.isPending || name.trim().length < 2}
        >
          {create.isPending ? 'جارٍ الإنشاء' : 'أنشئ'}
        </button>
        <button className="btn" onClick={onClose}>
          إلغاء
        </button>
      </div>
    </Modal>
  );
}

/* ==========================================================================
   رسالة مباشرة
   ========================================================================== */

function DirectModal({
  onClose,
  onOpened,
}: {
  onClose: () => void;
  onOpened: (c: ConversationView) => void;
}) {
  const { assignable } = useTeamDirectory();
  const session = useSession();
  const [error, setError] = useState<string | null>(null);

  const open = useMutation({
    mutationFn: (userId: string) =>
      api<ConversationView>('/chat/direct', { method: 'POST', body: { userId } }),
    onSuccess: onOpened,
    onError: (e) => setError(e instanceof Error ? e.message : 'تعذّر الفتح.'),
  });

  const others = assignable.filter((m) => m.userId !== session.user.id);

  return (
    <Modal title="رسالة مباشرة" onClose={onClose}>
      {error && <div className="alert alert-danger mb-4">{error}</div>}

      {others.length === 0 && <p className="mute center">لا زملاء في الشركة بعد.</p>}

      <div className="pick-list">
        {others.map((m) => (
          <button
            key={m.userId}
            className="pick-row"
            onClick={() => open.mutate(m.userId)}
            disabled={open.isPending}
          >
            <Avatar name={m.name} src={m.avatarUrl} size={30} />
            <span className="pick-row-text">
              <strong>{m.name}</strong>
              {m.jobTitle && <span className="tiny mute"> — {m.jobTitle}</span>}
            </span>
          </button>
        ))}
      </div>
    </Modal>
  );
}
